import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { Specialist } from '../../core/models/specialist';
import { StarRating } from '../../ui/star-rating';

const BULLET = /^\s*[*-]\s+/;

// Administrators write the description as plain text: blank lines split blocks, "* " starts a
// bullet, and a line ending in a colon heads what follows it.
function renderLines(lines: string[], key: string) {
    if (lines.length === 0) return null;
    return lines.every((line) => BULLET.test(line)) ? (
        <ul key={key}>
            {lines.map((line, item) => (
                <li key={item}>{line.replace(BULLET, '')}</li>
            ))}
        </ul>
    ) : (
        <p key={key}>{lines.join('\n')}</p>
    );
}

function renderDescription(description: string) {
    return description
        .split(/\r?\n\s*\r?\n/)
        .map((block) => block.trim().split(/\r?\n/).map((line) => line.trim()))
        .filter((lines) => lines[0] !== '')
        .map((lines, index) => {
            const [first, ...rest] = lines;
            if (index === 0 && rest.length === 0 && !first.endsWith(':') && !BULLET.test(first)) {
                return (
                    <p key={index} className="specialist-details__eyebrow">
                        {first}
                    </p>
                );
            }
            if (first.endsWith(':') && !BULLET.test(first)) {
                return (
                    <section key={index}>
                        <h3>{first.slice(0, -1)}</h3>
                        {renderLines(rest, 'rest')}
                    </section>
                );
            }
            return renderLines(lines, String(index));
        });
}

export function SpecialistDetails({ specialist, onClose }: { specialist: Specialist; onClose: () => void }) {
    const language = useLanguage();
    const { t } = useTranslation();
    const dialog = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    const description = language === 'kk' ? specialist.descriptionKk : specialist.descriptionRu;

    useEffect(() => {
        const element = dialog.current!;
        const previousFocus = document.activeElement;
        const previousPadding = document.body.style.paddingRight;
        // The stylesheet hides the page's scrollbar while this is open; the padding keeps the
        // page from widening into the space it leaves.
        document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
        element.showModal();

        return () => {
            element.close();
            document.body.style.paddingRight = previousPadding;
            if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
        };
    }, []);

    return (
        <dialog
            ref={dialog}
            className="specialist-details"
            aria-labelledby={titleId}
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div className="specialist-details__panel">
                <button
                    type="button"
                    className="specialist-details__close"
                    aria-label={t('site.spec.close')}
                    onClick={onClose}
                    autoFocus
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>

                <header className="specialist-details__profile">
                    <img className="specialist-details__photo" src={specialist.photoUrl} alt="" />
                    <div className="specialist-details__identity">
                        <h2 id={titleId}>{specialist.displayName}</h2>
                        {specialist.jobTitle && <p className="specialist-details__job">{specialist.jobTitle}</p>}
                        <p className="specialist-details__city">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
                                <circle cx="12" cy="9.5" r="2.5" />
                            </svg>
                            {t(specialist.city === 'Astana' ? 'site.city.astana' : 'site.city.almaty')}
                        </p>
                        {specialist.rating !== null ? (
                            <StarRating
                                stars={specialist.rating}
                                label={t('site.spec.rating').replace('{stars}', String(specialist.rating))}
                                className="specialist-details__rating"
                            />
                        ) : (
                            <p className="specialist-details__unrated">{t('site.spec.unrated')}</p>
                        )}
                    </div>
                </header>

                <div className="specialist-details__body" lang={language}>
                    {description && renderDescription(description)}
                </div>

                <footer className="specialist-details__footer">
                    <Link to={`/${language}/book?specialist=${specialist.id}`} className="spec-btn" onClick={onClose}>
                        {t('site.spec.book')}
                    </Link>
                </footer>
            </div>
        </dialog>
    );
}
